import { useCallback, useEffect, useRef, useState } from 'react';
import { type SetElement, useRect } from 'react-use-rect';
import type { TableLayout } from '../../../common';

const LAYOUT_CHANGE_THRESHOLD_PERCENT = 0.7;

export function useTableLayout({
  derivedLayout,
  itemsCount
}: {
  derivedLayout: TableLayout;
  itemsCount: number;
}): {
  tableLayout: TableLayout;
  setItemValueWidth: (index: number, value: number) => void;
  tableContainerRef: SetElement;
} {
  const [tableLayout, setTableLayout] = useState(derivedLayout);
  const lastItemsCountRef = useRef<number>(itemsCount);
  const tableWidthRef = useRef<number | null>(null);
  const itemsValuesWidthsRef = useRef<Array<number | null>>(
    Array(itemsCount).fill(null)
  );
  const animationFrameRef = useRef<number | null>(null);
  const lastLayoutOverrideRef = useRef<boolean | null>(null);

  const [tableContainerRef] = useRect(
    (rect) => {
      if (tableWidthRef.current === rect.width) {
        return;
      }

      tableWidthRef.current = rect.width;
      recalculateLayout();
    },
    { resize: true }
  );

  const recalculateLayout = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      if (!tableWidthRef.current) {
        return;
      }

      const shouldOverrideLayout = itemsValuesWidthsRef.current.some(
        (valueWidth) =>
          (valueWidth ?? 0) >=
          tableWidthRef.current! * LAYOUT_CHANGE_THRESHOLD_PERCENT
      );

      if (lastLayoutOverrideRef.current === shouldOverrideLayout) {
        return;
      }

      lastLayoutOverrideRef.current = shouldOverrideLayout;

      if (tableLayout === 'horizontal' && shouldOverrideLayout) {
        setTableLayout('vertical');
      } else if (!shouldOverrideLayout && tableLayout !== derivedLayout) {
        setTableLayout(derivedLayout);
      }

      animationFrameRef.current = null;
    });
  }, [tableLayout, derivedLayout]);

  const setItemValueWidth = useCallback(
    (itemIndex: number, newValueWidth: number) => {
      if (itemIndex < 0 || itemIndex >= itemsValuesWidthsRef.current.length) {
        return;
      }

      itemsValuesWidthsRef.current[itemIndex] = newValueWidth;
      recalculateLayout();
    },
    [recalculateLayout]
  );

  useEffect(() => {
    if (lastItemsCountRef.current === itemsCount) {
      return;
    }

    lastItemsCountRef.current = itemsCount;
    itemsValuesWidthsRef.current = Array(itemsCount).fill(null);
    lastLayoutOverrideRef.current = null;
  }, [itemsCount]);

  return { tableLayout, setItemValueWidth, tableContainerRef };
}
